import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import MiniChat from './MiniChat';

interface ChatConversation {
  chatId: string;
  therapistId: string;
  therapistName: string;
  otherMemberId: string;
  otherMemberName: string;
  otherMemberRole: string;
  unreadCount: number;
  lastMessage?: {
    text: string;
    timestamp: Date;
    senderId: string;
  };
}

interface OpenChat extends ChatConversation {
  isMinimized: boolean;
}

interface Props {
  currentUserId: string;
  currentUserName: string;
  currentUserRole: string;
  isTherapist: boolean;
}

const ChatWidget: React.FC<Props> = ({ currentUserId, currentUserName, currentUserRole, isTherapist }) => {
  const [isListOpen, setIsListOpen] = useState(false);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);

  // Escuta todas as conversas do usuário
  useEffect(() => {
    if (!currentUserId) return;

    const chatsRef = collection(db, 'chats');
    const allChatsMap = new Map<string, ChatConversation>();
    let unsubscribeTherapist: (() => void) | null = null;
    let unsubscribeOther: (() => void) | null = null;

    const updateConversations = () => {
      const chatsList = Array.from(allChatsMap.values());
      // Ordena por updatedAt se disponível
      chatsList.sort((a, b) => {
        if (!a.lastMessage?.timestamp || !b.lastMessage?.timestamp) return 0;
        return b.lastMessage.timestamp.getTime() - a.lastMessage.timestamp.getTime();
      });
      
      const unreadTotal = chatsList.reduce((sum, chat) => sum + chat.unreadCount, 0);
      setConversations(chatsList);
      setTotalUnread(unreadTotal);
    };

    // Query 1: Chats onde o usuário é therapist
    const therapistQuery = query(
      chatsRef,
      where('therapistId', '==', currentUserId),
      orderBy('updatedAt', 'desc'),
      limit(20)
    );

    unsubscribeTherapist = onSnapshot(therapistQuery, (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const unreadCount = data.unreadCount?.[currentUserId] || 0;

        const participants = data.participants || [];
        const otherParticipant = participants.find((p: any) => p.userId !== currentUserId);

        if (otherParticipant) {
          allChatsMap.set(data.chatId, {
            chatId: data.chatId,
            therapistId: data.therapistId,
            therapistName: participants.find((p: any) => p.userId === data.therapistId)?.name || 'Terapeuta',
            otherMemberId: data.otherMemberId,
            otherMemberName: otherParticipant.name,
            otherMemberRole: otherParticipant.role,
            unreadCount,
            lastMessage: data.lastMessage ? {
              text: data.lastMessage.text,
              timestamp: data.lastMessage.timestamp?.toDate() || new Date(),
              senderId: data.lastMessage.senderId,
            } : undefined,
          });
        }
      });
      updateConversations();
    });

    // Query 2: Chats onde o usuário é otherMember
    const otherQuery = query(
      chatsRef,
      where('otherMemberId', '==', currentUserId),
      orderBy('updatedAt', 'desc'),
      limit(20)
    );

    unsubscribeOther = onSnapshot(otherQuery, (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const unreadCount = data.unreadCount?.[currentUserId] || 0;

        const participants = data.participants || [];
        const otherParticipant = participants.find((p: any) => p.userId !== currentUserId);

        if (otherParticipant) {
          allChatsMap.set(data.chatId, {
            chatId: data.chatId,
            therapistId: data.therapistId,
            therapistName: participants.find((p: any) => p.userId === data.therapistId)?.name || 'Terapeuta',
            otherMemberId: data.otherMemberId,
            otherMemberName: otherParticipant.name,
            otherMemberRole: otherParticipant.role,
            unreadCount,
            lastMessage: data.lastMessage ? {
              text: data.lastMessage.text,
              timestamp: data.lastMessage.timestamp?.toDate() || new Date(),
              senderId: data.lastMessage.senderId,
            } : undefined,
          });
        }
      });
      updateConversations();
    });

    return () => {
      if (unsubscribeTherapist) unsubscribeTherapist();
      if (unsubscribeOther) unsubscribeOther();
    };
  }, [currentUserId]);

  const openChat = (conversation: ChatConversation) => {
    // Verifica se o chat já está aberto
    const existingChatIndex = openChats.findIndex(chat => chat.chatId === conversation.chatId);
    
    if (existingChatIndex !== -1) {
      // Se já estiver aberto, expande ele
      setOpenChats(prev => prev.map((chat, index) => 
        index === existingChatIndex ? { ...chat, isMinimized: false } : chat
      ));
    } else {
      // Se não estiver aberto, adiciona com isMinimized = false
      setOpenChats(prev => [...prev, { ...conversation, isMinimized: false }]);
    }
    setIsListOpen(false);
  };

  const closeChat = (chatId: string) => {
    setOpenChats(prev => prev.filter(chat => chat.chatId !== chatId));
  };

  const toggleMinimizeChat = (chatId: string) => {
    setOpenChats(prev => prev.map(chat => 
      chat.chatId === chatId ? { ...chat, isMinimized: !chat.isMinimized } : chat
    ));
  };

  const formatLastMessageTime = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Agora';
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  return (
    <>
      {/* Lista de conversas */}
      {isListOpen && (
        <div className="fixed inset-x-0 bottom-0 md:inset-x-auto md:bottom-24 md:right-6 w-full md:w-80 max-h-[75vh] md:max-h-96 bg-white rounded-t-2xl md:rounded-lg shadow-2xl border border-gray-200 z-50 overflow-hidden flex flex-col">
          <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <h3 className="font-semibold text-base md:text-base">Conversas</h3>
            <button
              onClick={() => setIsListOpen(false)}
              className="p-2 hover:bg-purple-700 active:bg-purple-800 rounded-lg transition-colors"
              aria-label="Fechar lista de conversas"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div
            className="overflow-y-auto overscroll-contain min-h-0"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Nenhuma conversa ainda
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.chatId}
                  onClick={() => openChat(conv)}
                  className="w-full p-4 md:p-3 hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100 text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${conv.unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                          {conv.otherMemberName}
                        </p>
                        {conv.unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">
                            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                          </span>
                        )}
                      </div>
                      {conv.lastMessage && (
                        <p className={`text-xs truncate mt-0.5 ${conv.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                          {conv.lastMessage.senderId === currentUserId ? 'Você: ' : ''}
                          {conv.lastMessage.text}
                        </p>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {formatLastMessageTime(conv.lastMessage.timestamp)}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Mini chats abertos */}
      <div className="fixed bottom-6 right-24 flex flex-row-reverse gap-2 z-[60]">
        {openChats.map((chat, index) => (
          <MiniChat
            key={chat.chatId}
            conversation={chat}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            currentUserRole={currentUserRole}
            onClose={() => closeChat(chat.chatId)}
            isMinimized={chat.isMinimized}
            onToggleMinimize={() => toggleMinimizeChat(chat.chatId)}
          />
        ))}
      </div>

      {/* Botão flutuante principal */}
      <button
        onClick={() => setIsListOpen(!isListOpen)}
        className={`fixed bottom-6 right-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-2xl transition-all z-40 ${
          openChats.length > 0 ? 'hidden md:flex' : 'flex'
        }`}
        title="Conversas"
      >
        <MessageCircle className="w-6 h-6" />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>
    </>
  );
};

export default ChatWidget;
